import { useState } from 'react'
import { toast } from 'react-toastify'
import { AuthService } from '../../../services/auth.service'
import { AxiosService } from '../../../services/axios.service'
import Button from '../../Button/Button'
import OTPVerification from '../OTPVerification'
import style from '../phone-verification.module.scss'
import type { AxiosError } from 'axios'
import { ApiErrorResponse } from '../../../types/api.types'

const authService = AuthService.getInstance()

export interface LoginProps {
  onSuccess?: () => void
  onSwitchToSignup?: () => void
}

enum LoginStep {
  EMAIL_INPUT = 'EMAIL_INPUT',
  OTP_VERIFICATION = 'OTP_VERIFICATION',
}

export const Login = ({ onSuccess, onSwitchToSignup }: LoginProps) => {
  const [currentStep, setCurrentStep] = useState<LoginStep>(LoginStep.EMAIL_INPUT)
  const [isLoading, setIsLoading] = useState(false)

  // Form fields
  const [email, setEmail] = useState<string>('')

  // Validation errors
  const [emailError, setEmailError] = useState<string>('')

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError('Email is required')
      return false
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address')
      return false
    }
    return true
  }

  const sendOTP = async (e?: React.FormEvent) => {
    e?.preventDefault()

    if (!validateEmail()) {
      return
    }

    try {
      setIsLoading(true)
      await authService.sendOTP(undefined, undefined, email, 'email')
      setCurrentStep(LoginStep.OTP_VERIFICATION)
      toast.success('OTP sent successfully!')
    } catch (_error) {
      const error = _error as AxiosError<ApiErrorResponse>
      toast.error(error.response?.data?.message || 'Failed to send OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const verifyOTP = async (otp: string) => {
    try {
      setIsLoading(true)

      const res = await authService.verifyOTP({
        email,
        otp,
        via: 'email',
      })

      if (res.status === 200) {
        const axiosService = AxiosService.getInstance()
        axiosService.setAccessToken(res.data.accessToken)
        axiosService.setRefreshToken(res.data.refreshToken)
        await authService.getUserDataAndUpdateStore()
        reset()
        onSuccess?.()
      } else {
        toast.error('Invalid OTP')
      }
    } catch (_error) {
      const error = _error as AxiosError<ApiErrorResponse>
      toast.error(error.response?.data?.message || 'Invalid OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (email) {
      await sendOTP()
    }
  }

  const reset = () => {
    setCurrentStep(LoginStep.EMAIL_INPUT)
    setEmail('')
    setEmailError('')
  }

  return (
    <>
        {currentStep === LoginStep.EMAIL_INPUT && (
          <form className={style.form} onSubmit={sendOTP}>
            <p className={style.description}>Enter your email to receive a verification code</p>

            <div className={style.emailInputContainer}>
              <input
                type="email"
                className={style.emailInput}
                placeholder="Email Address"
                value={email}
                onChange={e => {
                  setEmail(e.target.value)
                  setEmailError('')
                }}
              />
              {emailError && <span className={style.errorMessage}>{emailError}</span>}
            </div>

            <div className={style.buttonGroup}>
              <Button
                label={isLoading ? 'Sending...' : 'Send OTP'}
                type="submit"
                fullWidth={true}
                disabled={isLoading}
              />
              {onSwitchToSignup && (
                <button
                  type="button"
                  className={style.linkButton}
                  onClick={onSwitchToSignup}
                  disabled={isLoading}
                >
                  Don't have an account? Sign Up
                </button>
              )}
            </div>
          </form>
        )}

        {currentStep === LoginStep.OTP_VERIFICATION && (
          <OTPVerification
            email={email}
            isLoading={isLoading}
            onVerify={verifyOTP}
            onResend={handleResendOTP}
            onBack={() => setCurrentStep(LoginStep.EMAIL_INPUT)}
            backButtonText="Change Email"
          />
        )}
      </>
  )
}

export default Login
